import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  useAnnexureFillingNavOptional,
  type AnnexureFillingNavPayload,
} from '../contexts/AnnexureFillingNavContext';
import { VmsWorkspaceProvider, useVmsWorkspace } from '../contexts/VmsWorkspaceContext';
import { VmsWorkspaceSelectionSync } from '../hooks/useApplyVendorInitiationIdFromSearchParams';
import VmsThreadHeader from './vms/VmsThreadHeader';
import { useFocusTrap } from '../utils/accessibility';
import {
  getVmsDefaultLandingPath,
  getRolesVms,
  hasVmsRole,
  isStakeholderOnlyOperationalUser,
  isVmsAdminOnlyPath,
  isVmsAdminUser,
  isVmsContractManagementPath,
  shouldHideVmsOngoingForUser,
} from '../utils/vmsRole';
import {
  canAccessVmsDashboard,
  filterVmsNavLinkForUser,
  isVmsPathAllowedForUser,
  showVmsOngoingInNav,
  showVmsOnboardingInNav,
  showVmsRegisterInNav,
} from '../constants/vmsAccessMatrix';
import {
  MANAGEMENT2_CERTIFICATIONS_LIST_PATH,
  MANAGEMENT2_FLOW_STAKEHOLDERS_LIST_PATH,
} from '../constants/management2Nav';
import { shouldHideVmsThreadHeader } from '../pages/vms/vmsServiceJourneyModel';
import {
  VMS_ADMIN_CONFIG_ROUTES,
  isOutsourcingRegisterPath,
} from '../constants/vmsAdminConfigNav';
import {
  LayoutDashboard,
  BadgeCheck,
  AlignJustify,
  FileText,
  X,
  Users,
  Folder,
  User,
  Clock,
  Hexagon,
  Power,
  RectangleVertical,
  ListTodo,
  CalendarRange,
  Pencil,
} from 'lucide-react';
import {
  PRE_ONBOARDING_GROUP,
  ONBOARDING_GROUP,
  type VmsNavChild,
  type VmsNavItem,
  type VmsNavSublink,
  ONGOING_MANAGEMENT_LABEL,
  ONGOING_PERIODIC_REVIEW_LABEL,
  ONGOING_VENDOR_PERFORMANCE_REVIEW_LABEL,
  isVmsManagementMenuPath,
} from './vms/vmsNavModel';
import {
  ONGOING_VENDOR_PERIODIC_HUB_HREF,
  ONGOING_VENDOR_PERFORMANCE_HUB_HREF,
} from '../pages/vms/ongoingAnnexureRoutes';
import { VmsHorizontalPrimaryNav, VmsTopBrandRow } from './vms/VmsAppChrome';

function ongoingManagementNavGroup(): Extract<VmsNavItem, { type: 'group' }> {
  return {
    type: 'group',
    name: ONGOING_MANAGEMENT_LABEL,
    icon: CalendarRange,
    children: [
      {
        name: ONGOING_VENDOR_PERFORMANCE_REVIEW_LABEL,
        href: ONGOING_VENDOR_PERFORMANCE_HUB_HREF,
        icon: CalendarRange,
      },
      {
        name: ONGOING_PERIODIC_REVIEW_LABEL,
        href: ONGOING_VENDOR_PERIODIC_HUB_HREF,
        icon: CalendarRange,
      },
    ],
  };
}

function mergeAnnexureNavIntoOngoingChildren(
  base: VmsNavChild[],
  payload: AnnexureFillingNavPayload | null,
): VmsNavChild[] {
  if (!payload || payload.variant !== 'ongoing') return base;
  const setupPath = payload.setupHref.split('?')[0];
  const idx = base.findIndex((c) => {
    const childPath = c.href.split('?')[0];
    return setupPath === childPath || setupPath.startsWith(`${childPath}/`);
  });
  if (idx === -1) return base;
  const next = [...base];
  const sublinks: VmsNavSublink[] = [...payload.annexureLinks];
  next[idx] = { ...base[idx], href: payload.setupHref, sublinks };
  return next;
}

function mergeAnnexureNavIntoOnboardingChildren(
  base: VmsNavChild[],
  payload: AnnexureFillingNavPayload | null,
): VmsNavChild[] {
  if (!payload || payload.variant !== 'onboarding') return base;
  const idx = base.findIndex((c) => c.name === 'Annexure filling');
  if (idx === -1) return base;
  const next = [...base];
  const sublinks: VmsNavSublink[] = [...payload.annexureLinks];
  next[idx] = { ...base[idx], href: payload.setupHref, sublinks };
  return next;
}

function applyAnnexureNavPayloadToGroups(
  items: VmsNavItem[],
  payload: AnnexureFillingNavPayload | null,
): VmsNavItem[] {
  if (!payload) return items;
  return items.map((item) => {
    if (item.type !== 'group') return item;
    const children =
      payload.variant === 'ongoing'
        ? mergeAnnexureNavIntoOngoingChildren(item.children, payload)
        : mergeAnnexureNavIntoOnboardingChildren(item.children, payload);
    if (children === item.children) return item;
    return { ...item, children };
  });
}

function omitVmsAdminOnlyNavLinks(items: VmsNavItem[]): VmsNavItem[] {
  return items.filter((item) => {
    if (item.type === 'group') return true;
    if (isVmsContractManagementPath(item.href)) return true;
    if (item.name === 'DPIA Management' || item.href === '/vms/dpia-management') return true;
    return !isVmsAdminOnlyPath(item.href);
  });
}

/** Primary-strip links hidden for VMS role `admin` (governance uses admin-config tabs instead). */
const VMS_ADMIN_HIDDEN_PRIMARY_LINK_HREFS = new Set([
  '/vms/vendor-initiation-field-options',
  '/vms/user-management',
]);

function shouldHideGlobalThreadStrip(pathname: string): boolean {
  if (shouldHideVmsThreadHeader(pathname)) return true;
  if (isOutsourcingRegisterPath(pathname)) return true;
  if (isVmsManagementMenuPath(pathname)) return true;
  return false;
}

function VmsWorkspaceThreadStrip() {
  const location = useLocation();
  const { workspacePickerVisible, selectedVendorInitiationId, cachedSummary } = useVmsWorkspace();
  if (shouldHideGlobalThreadStrip(location.pathname)) return null;
  if (!workspacePickerVisible || selectedVendorInitiationId == null) return null;
  const loading = cachedSummary == null || cachedSummary.id !== selectedVendorInitiationId;
  return <VmsThreadHeader summary={loading ? null : cachedSummary} loading={loading} />;
}

const VMSLayout: React.FC = () => {
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const { user, logout, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const modalRef = useRef<HTMLDivElement>(null);
  const annexureNavPayload = useAnnexureFillingNavOptional();

  // Refresh user once when VMS shell mounts so roles_vms / VMS access is current.
  const vmsUserRefreshDoneRef = useRef(false);
  useEffect(() => {
    if (vmsUserRefreshDoneRef.current) return;
    vmsUserRefreshDoneRef.current = true;
    void refreshUser?.();
  }, [refreshUser]);

  const isBusinessOwner = hasVmsRole(user, 'business_owner');
  const isBusinessHead = hasVmsRole(user, 'business_head');
  const isLegal = hasVmsRole(user, 'legal');
  const isDpo = hasVmsRole(user, 'dpo');
  const isKycTeam = hasVmsRole(user, 'kyc_team');
  const isSmf = hasVmsRole(user, 'smf');
  const isStakeholder = hasVmsRole(user, 'stakeholder');
  const isVmsAdmin = isVmsAdminUser(user);
  const canUseOutsourcingRegister = Boolean(user) && showVmsRegisterInNav(user);
  const hasNoAssignedPages = getRolesVms(user).length === 0 && !canUseOutsourcingRegister;

  const sidebarBrandHref = isVmsAdmin ? '/vms/dashboard' : getVmsDefaultLandingPath(user);

  // Matrix-driven route guard (multi-role unions paths). Business owners may open all VMS routes.
  useEffect(() => {
    const path = location.pathname;
    if (!path.startsWith('/vms') || !user) return;

    if (shouldHideVmsOngoingForUser(user) && (path === '/vms/ongoing' || path.startsWith('/vms/ongoing/'))) {
      navigate(getVmsDefaultLandingPath(user), { replace: true });
      return;
    }

    if (isBusinessOwner) return;

    if (path === '/vms') {
      navigate(getVmsDefaultLandingPath(user), { replace: true });
      return;
    }

    if (path === '/vms/dashboard' && !canAccessVmsDashboard(user)) {
      navigate(getVmsDefaultLandingPath(user), { replace: true });
      return;
    }

    if (!isVmsPathAllowedForUser(user, path)) {
      navigate(getVmsDefaultLandingPath(user), { replace: true });
    }
  }, [user, isBusinessOwner, isVmsAdmin, location.pathname, navigate]);

  // Roles with no assigned pages: redirect any deep link to /vms (index shows message)
  useEffect(() => {
    if (!user || !hasNoAssignedPages) return;
    if (location.pathname !== '/vms') {
      navigate('/vms', { replace: true });
    }
  }, [user, hasNoAssignedPages, location.pathname, navigate]);

  // Trap focus in logout modal when open
  useFocusTrap(modalRef, showLogoutModal);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showLogoutModal) {
        setShowLogoutModal(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showLogoutModal]);

  const handleLogoutConfirm = () => {
    logout();
    setShowLogoutModal(false);
    navigate('/login');
  };

  // VMS Navigation: Pre onboarding flyout; Onboarding is a direct link to the vendor hub.
  const navigation = useMemo((): VmsNavItem[] => {
    const onboardingNavLink: VmsNavItem = {
      type: 'link',
      name: ONBOARDING_GROUP,
      href: '/vms/onboarding',
      icon: Clock,
    };

    /** Pre Onboarding top-nav flyout — only these two links are shown (routes for other modules unchanged). */
    const preOnboardingFlyoutChildren: VmsNavChild[] = [
      {
        name: 'New Vendor Initiation',
        href: '/vms/pre-onboarding/vendor-initiation',
        icon: FileText,
      },
      {
        name: 'Pre Onboarding InProgress',
        href: '/vms/pre-onboarding/in-progress',
        icon: ListTodo,
      },
    ];
    /*
     * Legacy pre-onboarding flyout entries (hidden from dropdown; not removed from codebase).
     * const preOnboardingOwnerExtras: VmsNavChild[] = [
     *   { name: 'New vendor initiation', href: '/vms/vendor-initiation', icon: FileText, sublinks: [...] },
     *   { name: 'RFP Management', href: '/vms/rfp-management', icon: ClipboardList },
     *   { name: 'Prospective Vendors', href: '/vms/prospective-vendors', icon: Users },
     *   { name: 'Vendor proposals', href: '/vms/vendor-proposals', icon: FileStack },
     *   { name: 'Vendor evaluations', href: '/vms/vendor-evaluations', icon: BarChart3 },
     *   { name: 'Financial approval', href: '/vms/financial-approval', icon: Banknote },
     * ];
     */
    const management2CertificationsDuplicateNav: VmsNavItem[] = [
      { type: 'link', name: 'Certifications & licenses', href: MANAGEMENT2_CERTIFICATIONS_LIST_PATH, icon: BadgeCheck },
    ];
    const management2DuplicateNav: VmsNavItem[] = [
      ...management2CertificationsDuplicateNav,
      { type: 'link', name: 'Flow Stakeholders', href: MANAGEMENT2_FLOW_STAKEHOLDERS_LIST_PATH, icon: Users },
    ];
    const fullNavigation: VmsNavItem[] = [
      { type: 'link', name: 'Certifications & licenses', href: '/vms/certifications', icon: BadgeCheck },
      { type: 'link', name: 'Dashboard', href: '/vms/dashboard', icon: LayoutDashboard },
      { type: 'link', name: 'My Tray', href: '/vms/my-tray', icon: AlignJustify },
      { type: 'link', name: 'Register', href: VMS_ADMIN_CONFIG_ROUTES.register, icon: Hexagon },
      { type: 'group', name: PRE_ONBOARDING_GROUP, icon: User, children: preOnboardingFlyoutChildren },
      onboardingNavLink,
      ongoingManagementNavGroup(),
      { type: 'link', name: 'Flow Stakeholders', href: '/vms/flow-stakeholders', icon: Users },
      { type: 'link', name: 'Contract Management', href: '/vms/contract-management', icon: FileText },
      { type: 'link', name: 'Vendor Exit', href: '/vms/vendor-exit', icon: Power },
      {
        type: 'link',
        name: 'Annexure Management',
        href: VMS_ADMIN_CONFIG_ROUTES.annexureManagement,
        icon: Folder,
      },
      { type: 'link', name: 'DPIA Management', href: '/vms/dpia-management', icon: RectangleVertical },
      { type: 'link', name: 'Audit Logs', href: '/vms/audit-logs', icon: Pencil },
      ...management2DuplicateNav,
    ];

    /*
     * Legacy business-head pre-onboarding flyout extras (hidden from dropdown).
     * Vendor Initiation Requests, Pending annexure approvals, RFP, evaluations, FA, etc.
     */
    const preOnboardingProgressOnlyChildren = preOnboardingFlyoutChildren.filter(
      (c) => c.href !== '/vms/pre-onboarding/vendor-initiation',
    );

    /** Pure BH: assigned pre-onboarding progress, onboarding hub, ongoing assessments (no New Vendor Initiation). */
    const businessHeadNavigation: VmsNavItem[] = [
      { type: 'link', name: 'Dashboard', href: '/vms/dashboard', icon: LayoutDashboard },
      { type: 'link', name: 'Certifications & licenses', href: '/vms/certifications', icon: BadgeCheck },
      { type: 'link', name: 'My Tray', href: '/vms/my-tray', icon: AlignJustify },
      {
        type: 'group',
        name: PRE_ONBOARDING_GROUP,
        icon: User,
        children: preOnboardingProgressOnlyChildren,
      },
      onboardingNavLink,
      ongoingManagementNavGroup(),
    ];

    const businessHeadStakeholderNavigation: VmsNavItem[] = [
      { type: 'link', name: 'Dashboard', href: '/vms/dashboard', icon: LayoutDashboard },
      { type: 'link', name: 'Certifications & licenses', href: '/vms/certifications', icon: BadgeCheck },
      { type: 'link', name: 'My Tray', href: '/vms/my-tray', icon: AlignJustify },
      {
        type: 'group',
        name: PRE_ONBOARDING_GROUP,
        icon: User,
        children: preOnboardingProgressOnlyChildren,
      },
      onboardingNavLink,
      ongoingManagementNavGroup(),
    ];

    /** Stakeholder-only: pre-onboarding, onboarding, management — no Ongoing Management tab. */
    const stakeholderPreOnboardingChildren = isStakeholderOnlyOperationalUser(user)
      ? preOnboardingFlyoutChildren.filter((c) => c.href !== '/vms/pre-onboarding/vendor-initiation')
      : preOnboardingFlyoutChildren;

    const stakeholderNavigation: VmsNavItem[] = [
      { type: 'link', name: 'Dashboard', href: '/vms/dashboard', icon: LayoutDashboard },
      { type: 'link', name: 'My Tray', href: '/vms/my-tray', icon: AlignJustify },
      { type: 'link', name: 'Certifications & licenses', href: '/vms/certifications', icon: BadgeCheck },
      {
        type: 'group',
        name: PRE_ONBOARDING_GROUP,
        icon: User,
        children: stakeholderPreOnboardingChildren,
      },
      onboardingNavLink,
      ...(showVmsOngoingInNav(user) ? [ongoingManagementNavGroup()] : []),
    ];

    const adminManagementNav: VmsNavItem[] = [
      { type: 'link', name: 'Dashboard', href: '/vms/dashboard', icon: LayoutDashboard },
      /*
       * Temporarily hidden from Management flyout (routes unchanged):
       * { type: 'link', name: 'Pre Onboarding InProgress', href: '/vms/pre-onboarding/in-progress', icon: ListTodo },
       * { type: 'link', name: 'Vendor Initiation Requests', href: '/vms/vendor-initiation-requests', icon: ClipboardList },
       */
      { type: 'link', name: 'Register', href: VMS_ADMIN_CONFIG_ROUTES.register, icon: Hexagon },
      { type: 'link', name: 'Contract Management', href: '/vms/contract-management', icon: FileText },
      {
        type: 'link',
        name: 'Annexure Management',
        href: VMS_ADMIN_CONFIG_ROUTES.annexureManagement,
        icon: Folder,
      },
      { type: 'link', name: 'DPIA Management', href: '/vms/dpia-management', icon: RectangleVertical },
      { type: 'link', name: 'Audit Logs', href: '/vms/audit-logs', icon: Pencil },
    ];

    /** Pure Legal: assigned pre-onboarding + onboarding progress, Contract Management (no Ongoing tab). */
    const legalOnlyNavigation: VmsNavItem[] = [
      { type: 'link', name: 'Dashboard', href: '/vms/dashboard', icon: LayoutDashboard },
      { type: 'link', name: 'My Tray', href: '/vms/my-tray', icon: AlignJustify },
      {
        type: 'group',
        name: PRE_ONBOARDING_GROUP,
        icon: User,
        children: preOnboardingProgressOnlyChildren,
      },
      onboardingNavLink,
      { type: 'link', name: 'Contract Management', href: '/vms/contract-management', icon: FileText },
    ];

    /** Pure SMF: notification regulator approver — dashboard, My Tray, onboarding only. */
    const smfOnlyNavigation: VmsNavItem[] = [
      { type: 'link', name: 'Dashboard', href: '/vms/dashboard', icon: LayoutDashboard },
      { type: 'link', name: 'My Tray', href: '/vms/my-tray', icon: AlignJustify },
      onboardingNavLink,
    ];

    /** Pure KYC Team: onboarding + ongoing only (assigned Annexure 6 vendors). */
    const kycOnlyNavigation: VmsNavItem[] = [
      { type: 'link', name: 'Dashboard', href: '/vms/dashboard', icon: LayoutDashboard },
      { type: 'link', name: 'My Tray', href: '/vms/my-tray', icon: AlignJustify },
      onboardingNavLink,
      ongoingManagementNavGroup(),
    ];

    /** Pure DPO: assigned pre-onboarding + onboarding progress, DPIA Management (no Ongoing tab). */
    const dpoOnlyNavigation: VmsNavItem[] = [
      { type: 'link', name: 'Dashboard', href: '/vms/dashboard', icon: LayoutDashboard },
      { type: 'link', name: 'My Tray', href: '/vms/my-tray', icon: AlignJustify },
      {
        type: 'group',
        name: PRE_ONBOARDING_GROUP,
        icon: User,
        children: preOnboardingProgressOnlyChildren,
      },
      onboardingNavLink,
      { type: 'link', name: 'DPIA Management', href: '/vms/dpia-management', icon: RectangleVertical },
    ];

    /** DPO + Legal (no BH/BO/stakeholder/admin): onboarding progress + both governance modules. */
    const dpoLegalCombinedNavigation: VmsNavItem[] = [
      { type: 'link', name: 'Dashboard', href: '/vms/dashboard', icon: LayoutDashboard },
      { type: 'link', name: 'My Tray', href: '/vms/my-tray', icon: AlignJustify },
      {
        type: 'group',
        name: PRE_ONBOARDING_GROUP,
        icon: User,
        children: preOnboardingProgressOnlyChildren,
      },
      onboardingNavLink,
      { type: 'link', name: 'Contract Management', href: '/vms/contract-management', icon: FileText },
      { type: 'link', name: 'DPIA Management', href: '/vms/dpia-management', icon: RectangleVertical },
    ];

    let base: VmsNavItem[];
    if (isBusinessOwner) {
      base = isVmsAdmin ? fullNavigation : omitVmsAdminOnlyNavLinks(fullNavigation);
    } else if (isBusinessHead && isStakeholder) {
      base = [...businessHeadStakeholderNavigation, ...(isVmsAdmin ? adminManagementNav : [])];
    } else if (isBusinessHead) {
      base = [...businessHeadNavigation, ...(isVmsAdmin ? adminManagementNav : [])];
    } else if (
      isDpo &&
      isLegal &&
      !isBusinessHead &&
      !isBusinessOwner &&
      !isStakeholder &&
      !isVmsAdmin
    ) {
      base = dpoLegalCombinedNavigation;
    } else if (isLegal && !isBusinessHead && !isStakeholder && !isVmsAdmin && !isDpo) {
      base = legalOnlyNavigation;
    } else if (isDpo && !isBusinessHead && !isBusinessOwner && !isStakeholder && !isVmsAdmin && !isLegal) {
      base = dpoOnlyNavigation;
    } else if (
      isKycTeam &&
      !isBusinessHead &&
      !isBusinessOwner &&
      !isStakeholder &&
      !isVmsAdmin &&
      !isLegal &&
      !isDpo &&
      !isSmf
    ) {
      base = kycOnlyNavigation;
    } else if (
      isSmf &&
      !isBusinessHead &&
      !isBusinessOwner &&
      !isStakeholder &&
      !isVmsAdmin &&
      !isLegal &&
      !isDpo &&
      !isKycTeam
    ) {
      base = smfOnlyNavigation;
    } else if (isStakeholder) {
      base = [...stakeholderNavigation, ...(isVmsAdmin ? adminManagementNav : [])];
    } else if (isVmsAdmin) {
      base = fullNavigation;
    } else if (canUseOutsourcingRegister) {
      base = [{ type: 'link', name: 'Register', href: VMS_ADMIN_CONFIG_ROUTES.register, icon: Hexagon }];
    } else {
      base = [];
    }

    const withAnnexureNav = applyAnnexureNavPayloadToGroups(base, annexureNavPayload);
    const hideOngoing = !showVmsOngoingInNav(user);
    const hideOnboarding = !showVmsOnboardingInNav(user);
    const hideRegister = !showVmsRegisterInNav(user);
    const seenLinks = new Set<string>();
    return withAnnexureNav.filter((item) => {
      if (hideOngoing && item.type === 'group' && item.name === ONGOING_MANAGEMENT_LABEL) {
        return false;
      }
      if (hideOngoing && item.type === 'link') {
        if (item.name === ONGOING_MANAGEMENT_LABEL || item.href === '/vms/ongoing' || item.href === '/vms/ongoing-management') {
          return false;
        }
      }
      if (hideOnboarding && item.type === 'link') {
        if (item.name === ONBOARDING_GROUP || item.href === '/vms/onboarding') {
          return false;
        }
      }
      if (hideRegister && item.type === 'link' && isOutsourcingRegisterPath(item.href)) {
        return false;
      }
      if (item.type === 'link' && !filterVmsNavLinkForUser(user, item)) {
        return false;
      }
      if (item.type !== 'link') return true;
      if (seenLinks.has(item.href)) return false;
      seenLinks.add(item.href);
      return true;
    });
  }, [
    isBusinessOwner,
    isBusinessHead,
    isStakeholder,
    isLegal,
    isDpo,
    isKycTeam,
    isSmf,
    isVmsAdmin,
    canUseOutsourcingRegister,
    annexureNavPayload,
    location.search,
    user,
  ]);

  /** VMS admin: Register only in admin-config strip; hide legacy IMS-style admin links from primary nav. */
  const navigationForChrome = useMemo(() => {
    if (!isVmsAdmin) return navigation;
    return navigation.filter((item) => {
      if (item.type !== 'link') return true;
      if (isOutsourcingRegisterPath(item.href)) return false;
      if (VMS_ADMIN_HIDDEN_PRIMARY_LINK_HREFS.has(item.href)) return false;
      return true;
    });
  }, [navigation, isVmsAdmin]);

  return (
    <VmsWorkspaceProvider user={user}>
      <VmsWorkspaceSelectionSync />
    <div className="vms-app-layout flex h-screen flex-col overflow-hidden">
      {/* Skip to main content link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
      >
        Skip to main content
      </a>

      <div className="vms-app-header-bleed-inset shrink-0">
        <header className="vms-app-header">
          <VmsTopBrandRow
            homeHref={sidebarBrandHref}
            user={user}
            onLogoutClick={() => setShowLogoutModal(true)}
          />
          <VmsHorizontalPrimaryNav items={navigationForChrome} user={user} showAdminConfigNav={isVmsAdmin} />
        </header>
      </div>

      <VmsWorkspaceThreadStrip />

      <main
        id="main-content"
        className="relative flex-1 overflow-y-auto overflow-x-hidden focus:outline-none"
        role="main"
        tabIndex={-1}
      >
        <Outlet />
      </main>

      {/* Logout Modal */}
      {showLogoutModal && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="logout-modal-title"
          aria-describedby="logout-modal-description"
        >
          <div ref={modalRef} className="bg-white rounded-md shadow-lg w-96 p-6" role="document">
            <div className="flex justify-between items-center mb-4">
              <h3 id="logout-modal-title" className="text-lg font-medium text-gray-900">
                Logout
              </h3>
              <button
                type="button"
                onClick={() => setShowLogoutModal(false)}
                className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded"
                aria-label="Close logout confirmation dialog"
              >
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <p id="logout-modal-description" className="text-sm text-gray-900 mb-6">
              Are you sure you want to logout from your account?
            </p>
            <div className="flex justify-center space-x-3">
              <button
                type="button"
                onClick={() => setShowLogoutModal(false)}
                className="btn-secondary"
                aria-label="Cancel logout"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLogoutConfirm}
                className="btn-primary"
                aria-label="Confirm logout"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </VmsWorkspaceProvider>
  );
};

export default VMSLayout;

